import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const currencySchema = z.string().regex(/^[A-Z]{3}$/)
const safeTextSchema = z.string().min(1).max(160)

export const customerStatementRequestV1Schema = z.object({
  statementVersion: z.literal(1),
  customerId: entityIdSchema,
  fromDate: dateSchema,
  toDate: dateSchema,
  cursor: z.number().int().min(0).max(10_000),
  pageSize: z.number().int().min(1).max(50),
}).strict()

const paymentV1Schema = z.object({
  paymentAttemptId: entityIdSchema,
  amountMinor: z.number().int().safe().positive(),
  currency: currencySchema,
  state: z.enum(["processing", "succeeded", "failed", "reconciliation_required"]),
  paymentPath: z.enum(["hosted", "autopay"]),
  occurredAt: z.string().datetime({ offset: true }),
}).strict()

const invoiceV1Schema = z.object({
  invoiceId: entityIdSchema,
  billingProfileId: entityIdSchema,
  state: z.enum([
    "draft",
    "review_required",
    "tax_review_required",
    "finalized",
    "open",
    "partially_paid",
    "paid",
    "voided",
  ]),
  invoiceDate: z.string().datetime({ offset: true }),
  dueDate: dateSchema,
  totalMinor: z.number().int().safe().nonnegative(),
  amountPaidMinor: z.number().int().safe().nonnegative(),
  balanceDueMinor: z.number().int().safe().nonnegative(),
  branchIds: z.array(branchIdSchema).max(100),
  payments: z.array(paymentV1Schema).max(50),
}).strict()

const legalEntityGroupV1Schema = z.object({
  legalEntityId: legalEntityIdSchema,
  merchantName: safeTextSchema,
  currency: currencySchema,
  paymentDestination: z.object({
    provider: z.literal("stripe"),
    destinationCode: z.string().regex(/^stripe:[a-f0-9]{16}$/),
  }).strict().nullable(),
  invoicedMinor: z.number().int().safe().nonnegative(),
  paidMinor: z.number().int().safe().nonnegative(),
  balanceDueMinor: z.number().int().safe().nonnegative(),
  invoices: z.array(invoiceV1Schema).max(50),
}).strict()

export const customerStatementV1Schema = z.object({
  statementVersion: z.literal(1),
  tenantId: tenantIdSchema,
  customerId: entityIdSchema,
  fromDate: dateSchema,
  toDate: dateSchema,
  observedAt: z.string().datetime({ offset: true }),
  legalEntityGroups: z.array(legalEntityGroupV1Schema).max(100),
  nextCursor: z.number().int().min(0).max(10_000).nullable(),
  hasMore: z.boolean(),
}).strict()

export type CustomerStatementRequestV1 = z.infer<
  typeof customerStatementRequestV1Schema
>
export type CustomerStatementV1 = z.infer<typeof customerStatementV1Schema>
